import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "./use-toast";
import { Loader2, CheckCircle2, AlertCircle, Sparkles, Ticket, Copy, Check, Package } from "lucide-react";

import { trackPurchaseEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

export default function PaymentSuccess({ setSelectedTab }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Phases: 'verifying' -> 'generating' -> 'done' | 'order_received' | 'error'
  const [phase, setPhase] = useState('verifying');
  const [generationStatus, setGenerationStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // Voucher purchases follow a different success copy path. Track whether
  // this session was a map purchase or a voucher one so the heading/body
  // text below stays accurate.
  const [purchaseKind, setPurchaseKind] = useState('map');
  const [voucher, setVoucher] = useState(null); // { code, amountCents }
  const [voucherCopied, setVoucherCopied] = useState(false);
  const [printOrderInfo, setPrintOrderInfo] = useState(null); // { size, heigth, width }
  const isVerifying = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const session_id = searchParams.get('session_id');
    if (!session_id) {
      trackPurchaseEvents.checkoutAbandoned();
      navigate('/');
      return;
    }

    const verifyPayment = async () => {
      if (isVerifying.current) return;
      isVerifying.current = true;

      try {
        const verifyResponse = await fetch(`${API_URL}/payment-success?session_id=${session_id}`);
        if (!verifyResponse.ok) {
          trackPurchaseEvents.paymentFailed("Verfy response not ok");
          throw new Error('Payment verification failed');
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        const verifyJson = await verifyResponse.json();
        // Voucher purchase short-circuit — no map regeneration, just toast
        // the new code and bounce back to the dashboard. The vouchers dialog
        // will pick the new entry up on next open.
        if (verifyJson.kind === 'voucher') {
          setPurchaseKind('voucher');
          setVoucher({
            code: verifyJson.voucherCode,
            amountCents: verifyJson.amountCents,
            isPublic: !!verifyJson.isPublic,
            buyerEmail: verifyJson.buyerEmail || null,
          });
          setPhase('done');
          // Don't auto-redirect for voucher purchases — the user might want
          // to copy the code, take a screenshot, etc. They click Go back
          // when ready (rendered in the JSX below).
          return;
        }
        const { needsRegeneration, imageId, heigth, width, size, kind } = verifyJson;
        const isPrintOrder = kind === 'print';

        // For print orders the user shouldn't have to wait — show the
        // confirmation immediately and kick off image generation in the
        // background. The digital file shows up in Your Maps once the
        // render finishes.
        //
        // Always go through /generate_final_store for prints: it renders
        // at FINAL_DPI (300), while /store-image just promotes whatever
        // preview-DPI bytes already exist. Without this the L print
        // would otherwise reuse the ~100-DPI preview and produce a file
        // that's far too small to print at 80x60cm.
        if (isPrintOrder) {
          setPrintOrderInfo({ size, heigth, width });
          setPhase('order_received');
          fetch(`${API_URL}/generate_final_store`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            // keepalive lets the request survive even if the user clicks
            // "View Your Maps" before the (long-running) regen finishes.
            keepalive: true,
            body: JSON.stringify({ imageId, heigth, width, size }),
          }).then(r => {
            if (!r.ok) {
              trackPurchaseEvents.paymentSuccessError(size, true, "gen/store error (print bg)");
            }
          }).catch(() => {/* fire-and-forget */});
          trackPurchaseEvents.paymentSuccess(size, true);
          try {
            const cur = parseInt(localStorage.getItem('unseenMaps') || '0', 10);
            const next = (Number.isNaN(cur) ? 0 : cur) + 1;
            localStorage.setItem('unseenMaps', String(next));
            // 'storage' only fires in OTHER tabs; broadcast to App.js
            // in the same tab so the badge state updates immediately
            // (and the history-tab redirect's clear-effect can fire).
            window.dispatchEvent(new Event('unseenMapsChanged'));
          } catch (_e) { /* no-op */ }
          return;
        }

        if (needsRegeneration) {
          setPhase('generating');
          const response = await fetch(`${API_URL}/generate_final_store`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageId, heigth, width, size }),
          });

          if (!response.ok) {
            trackPurchaseEvents.paymentSuccessError(size, needsRegeneration, "gen/store error");
            throw new Error('Failed to generate and store image');
          }
        } else {
          const response = await fetch(`${API_URL}/store-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageId }),
          });

          if (!response.ok) {
            trackPurchaseEvents.paymentSuccessError(size, needsRegeneration, "store error");
            throw new Error('Failed to store image');
          }
        }
        trackPurchaseEvents.paymentSuccess(size, needsRegeneration);
        // Bump the unseen-maps counter so other tabs see a badge on the
        // "Your Maps" button. Same-tab clears it immediately on the
        // history-tab redirect below — that's intentional.
        try {
          const cur = parseInt(localStorage.getItem('unseenMaps') || '0', 10);
          const next = (Number.isNaN(cur) ? 0 : cur) + 1;
          localStorage.setItem('unseenMaps', String(next));
          window.dispatchEvent(new Event('unseenMapsChanged'));
        } catch (_e) { /* quota / private mode — no-op */ }
        setPhase('done');

        toast({
          title: "Success",
          description: "Payment successful! Your map is available in Previous Maps.",
          variant: "success",
          duration: 6000,
        });

        // Brief pause so the user actually sees the success state.
        await new Promise(r => setTimeout(r, 1500));

        setSelectedTab("history");
        navigate('/dashboard?tab=history', { replace: true });
      } catch (error) {
        setPhase('error');
        setErrorMessage(error.message);
        toast({
          title: "Error",
          description: "Failed to process payment: " + error.message,
          variant: "destructive",
          duration: 10000,
        });
        await new Promise(r => setTimeout(r, 2500));
        navigate('/dashboard', { replace: true });
      } finally {
        isVerifying.current = false;
      }
    };

    verifyPayment();
    return () => {
      isVerifying.current = true;
    };
  }, [navigate, toast, setSelectedTab]);

  // Mirror the dashboard polling pattern: while in 'generating', poll the
  // server-side state every 2s so the progress bar reflects real backend
  // progress (calibration → topography → geometries → save).
  useEffect(() => {
    if (phase !== 'generating') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setGenerationStatus(data);
      } catch (_e) { /* transient — keep polling */ }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase]);

  const pct = Math.max(0, Math.min(100, generationStatus?.progress || 0));
  const label = generationStatus?.label || 'Starting…';

  return (
    <div className="w-full flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30">
        <div className="mb-6 flex justify-center">
          {phase === 'verifying' && (
            <div className="h-20 w-20 rounded-full bg-blue-50 flex items-center justify-center ring-4 ring-blue-100">
              <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
            </div>
          )}
          {phase === 'generating' && (
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center ring-4 ring-purple-100">
              <Sparkles className="h-10 w-10 text-purple-500 animate-pulse" />
            </div>
          )}
          {phase === 'done' && purchaseKind === 'voucher' && (
            <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center ring-4 ring-gray-200">
              <Ticket className="h-10 w-10 text-gray-900" />
            </div>
          )}
          {phase === 'done' && purchaseKind !== 'voucher' && (
            <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center ring-4 ring-green-100">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
          )}
          {phase === 'order_received' && (
            <div className="h-20 w-20 rounded-full bg-emerald-50 flex items-center justify-center ring-4 ring-emerald-100">
              <Package className="h-10 w-10 text-emerald-600" />
            </div>
          )}
          {phase === 'error' && (
            <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center ring-4 ring-red-100">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
          )}
        </div>

        <h1 className="text-2xl font-bold mb-2 text-gray-900">
          {phase === 'verifying' && 'Confirming your payment'}
          {phase === 'generating' && 'Crafting your map'}
          {phase === 'done' && (purchaseKind === 'voucher' ? 'Voucher purchased!' : 'All set!')}
          {phase === 'order_received' && 'Order received!'}
          {phase === 'error' && 'Something went wrong'}
        </h1>

        <p className="text-gray-600 mb-6">
          {phase === 'verifying' && 'Hold tight while we verify with our payment provider.'}
          {phase === 'generating' && 'Rendering at the resolution you paid for. This usually takes 1–3 minutes.'}
          {phase === 'done' && purchaseKind === 'voucher' && voucher && (
            voucher.isPublic ? (
              <>An email with your voucher has been sent to <strong>{voucher.buyerEmail}</strong>. You can also copy the code below.</>
            ) : (
              <>Your new voucher is also saved in your profile. You can copy the code below.</>
            )
          )}
          {phase === 'done' && purchaseKind !== 'voucher' && 'Your map is ready in Previous Maps. Redirecting…'}
          {phase === 'order_received' && (
            <>Thanks for your order! We'll prepare your print and ship it within 7–10 business days. You'll get an email at each step.</>
          )}
          {phase === 'error' && (errorMessage || 'Please try again or contact support.')}
        </p>

        {phase === 'order_received' && printOrderInfo && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 mb-4 text-left text-sm">
            <div className="font-medium text-gray-900 mb-1">Order summary</div>
            <div className="text-gray-600">
              Size {printOrderInfo.size} — {printOrderInfo.heigth}×{printOrderInfo.width} cm
            </div>
            <div className="text-gray-500 text-xs mt-1">
              A digital copy will also be available in Your Maps as soon
              as rendering is done.
            </div>
          </div>
        )}

        {phase === 'order_received' && (
          <div className="flex justify-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setSelectedTab && setSelectedTab('history');
                navigate('/dashboard?tab=history', { replace: true });
              }}
              className="inline-flex items-center gap-2 rounded-2xl bg-gray-900 hover:bg-gray-700 text-white px-5 py-2 text-sm font-semibold"
            >
              View Your Maps
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard', { replace: true })}
              className="inline-flex items-center gap-2 rounded-2xl border border-gray-300 hover:bg-gray-50 text-gray-900 px-5 py-2 text-sm font-semibold"
            >
              Back to dashboard
            </button>
          </div>
        )}

        {phase === 'done' && purchaseKind === 'voucher' && voucher && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 mb-4 flex items-center justify-between gap-3">
            <div className="text-left">
              <code className="font-mono text-base font-semibold tracking-wide break-all">
                {voucher.code}
              </code>
              <p className="text-xs text-gray-500">€{(voucher.amountCents / 100).toFixed(2)} off</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(voucher.code);
                  setVoucherCopied(true);
                  setTimeout(() => setVoucherCopied(false), 1500);
                } catch (_e) { /* no-op */ }
              }}
              className={`flex-shrink-0 inline-flex items-center gap-1 text-sm rounded-lg border px-3 py-1.5 transition-colors ${
                voucherCopied ? 'border-gray-900 text-gray-900' : 'border-gray-300 hover:bg-white'
              }`}
            >
              {voucherCopied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
            </button>
          </div>
        )}

        {phase === 'done' && purchaseKind === 'voucher' && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                const isLoggedIn = !!localStorage.getItem('token');
                navigate(isLoggedIn ? '/dashboard' : '/', { replace: true });
              }}
              className="inline-flex items-center gap-2 rounded-2xl bg-gray-900 hover:bg-gray-700 text-white px-5 py-2 text-sm font-semibold"
            >
              Go back
            </button>
          </div>
        )}

        {phase === 'generating' && (
          <div className="space-y-2">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{label} ({pct}%)</p>
            <p className="text-xs text-gray-400 mt-4">
              You can safely close this tab — the render keeps going on the server and
              your map will appear in Previous Maps once it's done.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
